import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { CheckIcon, ChevronDownIcon } from '@radix-ui/react-icons';
import { CommandLoading } from 'cmdk';
import { useMemo, useState } from 'react';
import List from 'react-virtualized/dist/es/List';
import Spinner from '../spinner/Spinner';
import { Alert } from '../ui/alert';
import UIError from '@/errors/UIError';
import { Optional } from '@/utils/types';
import { Label } from '../ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

export type Item<Metadata> = {
	id: string;
	name: string;
	parentId: Optional<string>;
	metadata?: Optional<Metadata>;
};

type Props<Metadata> = {
	selectedItem: Optional<Item<Metadata>>;
	selectedItemRenderer?: (item: Item<Metadata>) => string;
	items?: Item<Metadata>[];
	loading?: boolean;
	onSearch?: (search: string) => void;
	onSelect?: (selected: Item<Metadata>) => void;
	defaultSearchValue?: string;
	placeholder?: string;
	error?: Error | null;
	orphanGroupName?: string;
	label?: Optional<string>;
};

export default function GroupedCombobox<Metadata = Record<string, string>>({
	selectedItem,
	selectedItemRenderer = item => item.name,
	items = [],
	loading,
	onSearch,
	onSelect,
	defaultSearchValue,
	placeholder = 'Select an item',
	orphanGroupName = 'Other',
	error,
	label
}: Props<Metadata>) {
	const [open, setOpen] = useState(false);
	const [searchValue, setSearchValue] = useState(defaultSearchValue);

	const groupedItems = useMemo(() => {
		const orphans: Item<Metadata>[] = [];
		const initialGroups: Record<string, Item<Metadata>[]> = {};

		if (!items) {
			return initialGroups;
		}

		const reduced = items.reduce((grouped, item) => {
			if (!item.parentId) {
				orphans.push(item);
				return grouped;
			}

			if (!grouped[item.parentId]) {
				grouped[item.parentId] = [];
			}

			grouped[item.parentId].push(item);
			return grouped;
		}, initialGroups);

		reduced[orphanGroupName] = orphans;

		return reduced;
	}, [items, orphanGroupName]);

	const grouppedItemsEntries = Object.entries(groupedItems);

	return (
		<div>
			{label && <Label className="block mb-2 ml-1">{label}</Label>}
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<Button
						variant="outline"
						role="combobox"
						aria-label={selectedItem?.name ?? placeholder}
						aria-expanded={open}
						disabled={loading}
						className="w-[300px] justify-between"
					>
						{selectedItem ? selectedItemRenderer(selectedItem) : placeholder}
						{!loading && (
							<ChevronDownIcon
								className={cn('ml-2 h-4 w-4 shrink-0 opacity-50', {
									'rotate-180': open
								})}
							/>
						)}
						{loading && <Spinner />}
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-[300px] p-0">
					<Command shouldFilter={false}>
						<CommandInput
							placeholder="Search items..."
							value={searchValue}
							onValueChange={search => {
								setSearchValue(search);
								onSearch?.(search);
							}}
						/>

						{error && (
							<>
								<Alert className="bg-red-300 w-[90%] mx-auto my-5">
									<span className="font-semibold">Error:</span> {error?.message}
								</Alert>
								<CommandList />
							</>
						)}

						{loading && (
							<>
								<CommandLoading className="p-10">
									<Spinner className="mx-auto" />
								</CommandLoading>
								<CommandList />
							</>
						)}
						{!loading && groupedItems && !error && (
							<>
								<CommandEmpty>No items found.</CommandEmpty>
								<CommandList>
									<List
										height={300}
										width={300}
										rowCount={grouppedItemsEntries.length}
										rowHeight={({ index }) => (grouppedItemsEntries[index][1].length + 1) * 30}
										rowRenderer={({ index, style }) => {
											const [parentId, nestedItems] = grouppedItemsEntries[index];

											if (!nestedItems || nestedItems.length === 0) {
												return null;
											}

											return (
												<CommandGroup style={style} key={parentId} heading={parentId}>
													{nestedItems?.map(item => (
														<CommandItem
															key={item.id}
															value={item.id}
															onSelect={currentValue => {
																const selectedItem = items.find(
																	el => el.id === currentValue
																);

																if (!selectedItem) {
																	throw new UIError(
																		`Selected item id: ${currentValue} not found in the list of items`
																	);
																}

																onSelect?.(selectedItem);
																setOpen(false);
															}}
														>
															<CheckIcon
																className={cn(
																	'mr-2 h-4 w-4',
																	selectedItem?.id === item.id
																		? 'opacity-100'
																		: 'opacity-0'
																)}
															/>
															<Tooltip>
																<TooltipTrigger asChild>
																	<span className="truncate flex-1 text-left cursor-pointer">
																		{item.name}
																	</span>
																</TooltipTrigger>
																<TooltipContent>
																	<p>{item.name}</p>
																</TooltipContent>
															</Tooltip>
														</CommandItem>
													))}
												</CommandGroup>
											);
										}}
									/>
								</CommandList>
							</>
						)}
					</Command>
				</PopoverContent>
			</Popover>
		</div>
	);
}
